import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, TextField } from '@mui/material'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import { formatar, Indicador, pontosDaLinha, variacao } from '../inicio/indicador'

interface Medida { nome: string; tabela: string; descricao?: string | null; formato?: string | null }
interface Coluna { nome: string; tipo?: string | null }
interface Props {
  dash: { id: number; type?: string; datasetId?: string | null; kpiMeasure?: string | null; kpiDateColumn?: string | null; kpiFormat?: string | null; kpiHigherIsBetter?: boolean }
  aoSalvar: () => void
}

/** No INFO.COLUMNS do Power BI, 9 é data/hora. Só coluna do tipo data serve: texto como "MES/ANO" o Power BI recusa. */
const ehData = (c: Coluna) => c.tipo === '9'
const formatoDaMedida = (m?: Medida | null) => (!m?.formato ? null : m.formato.includes('%') ? 'percentual' : /R\$|\$/.test(m.formato) ? 'moeda' : 'numero')

/** Gestão › um dashboard › indicador do card na página inicial. Só administrador do BI. */
export default function ConfigIndicador({ dash, aoSalvar }: Props) {
  const { notify } = useNotification()
  const [medidas, setMedidas] = useState<Medida[] | null>(null)
  const [tabelas, setTabelas] = useState<string[]>([])
  const [colunas, setColunas] = useState<Coluna[]>([])
  const [erroModelo, setErroModelo] = useState<string | null>(null)
  const [medida, setMedida] = useState(dash.kpiMeasure || '')
  const [tabela, setTabela] = useState(dash.kpiDateColumn ? dash.kpiDateColumn.split('[')[0] : '')
  const [coluna, setColuna] = useState(dash.kpiDateColumn || '')
  const [formato, setFormato] = useState(dash.kpiFormat || 'numero')
  const [maiorMelhor, setMaiorMelhor] = useState(dash.kpiHigherIsBetter ?? true)
  const [teste, setTeste] = useState<Indicador | string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  useEffect(() => {
    if (!dash.datasetId) return
    services.api.relatorioHtmlModelo(dash.id, 'medidas').then((r: any) => {
      if (r.fail || !r.data?.ok) { setErroModelo(r.data?.erro || r.error || 'Não foi possível ler o modelo.'); setMedidas([]); return }
      setMedidas(r.data.itens)
    })
    services.api.relatorioHtmlModelo(dash.id, 'tabelas').then((r: any) => { if (!r.fail && r.data?.ok) setTabelas(r.data.itens.map((t: any) => t.nome)) })
  }, [dash.id, dash.datasetId])

  useEffect(() => {
    setColunas([])
    if (!tabela) return
    services.api.relatorioHtmlModelo(dash.id, 'colunas', tabela).then((r: any) => {
      if (!r.fail && r.data?.ok) setColunas(r.data.itens.filter(ehData))
    })
  }, [dash.id, tabela])

  const medidaEscolhida = useMemo(() => (medidas || []).find(m => m.nome === medida) || null, [medidas, medida])
  const config = { medida, colunaData: coluna, formato, maiorMelhor }
  const pronto = !!medida && !!coluna
  const mudou = medida !== (dash.kpiMeasure || '') || coluna !== (dash.kpiDateColumn || '') || formato !== (dash.kpiFormat || 'numero') || maiorMelhor !== (dash.kpiHigherIsBetter ?? true)

  const testar = async () => {
    setOcupado(true); setTeste(null)
    const r = await services.api.novoPortalTestarIndicador(dash.id, config)
    setOcupado(false)
    setTeste(r.fail ? (r.error || 'Não foi possível testar.') : r.data)
  }
  const salvar = async (tirar = false) => {
    setOcupado(true)
    const r = await services.api.novoPortalConfigurarIndicador(dash.id, tirar ? { medida: '' } : config)
    setOcupado(false)
    if (r.fail) { notify(r.error || 'Não foi possível salvar o indicador.', { variant: 'error' }); return }
    notify(tirar ? 'Indicador retirado: o card fica só com o nome.' : 'Indicador salvo. Aparece no card de quem favoritar este dashboard.', { variant: 'success' })
    if (tirar) { setMedida(''); setTabela(''); setColuna(''); setTeste(null) }
    aoSalvar()
  }

  return (
    <section className="ng-cartao">
      <h2>Indicador do card</h2>
      <p className="ng-explic">O número que aparece no card deste dashboard na página inicial, para quem favoritar: os últimos 7 dias, a variação contra os 7 anteriores e as 12 semanas. Cada pessoa vê com o recorte dela.</p>
      {!dash.datasetId
        ? <p className="ng-frase">Grave o dataset nas configurações acima para escolher o indicador.</p>
        : erroModelo
          ? <p className="ng-frase">{erroModelo}</p>
          : (
            <>
              <div className="ng-campos">
                <div className="ng-campo largo"><span>Medida</span>
                  <Autocomplete size="small" options={medidas || []} loading={!medidas} value={medidaEscolhida}
                                getOptionLabel={m => m.nome} groupBy={m => m.tabela} isOptionEqualToValue={(a, b) => a.nome === b.nome}
                                onChange={(_, m) => { setMedida(m?.nome || ''); const f = formatoDaMedida(m); if (f) setFormato(f) }}
                                noOptionsText="Nenhuma medida com esse nome" loadingText="Lendo o modelo…"
                                renderInput={p => <TextField {...p} placeholder="Buscar medida" />} />
                  {medidaEscolhida?.descricao && <small>{medidaEscolhida.descricao}</small>}
                </div>
                <label className="ng-campo"><span>Tabela de datas</span>
                  <select value={tabela} onChange={e => { setTabela(e.target.value); setColuna('') }}>
                    <option value="">Escolha</option>
                    {tabelas.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="ng-campo"><span>Coluna de data</span>
                  <select value={coluna} onChange={e => setColuna(e.target.value)} disabled={!tabela}>
                    <option value="">Escolha</option>
                    {coluna && !colunas.some(c => c.nome === coluna) && <option value={coluna}>{coluna}</option>}
                    {colunas.map(c => <option key={c.nome} value={c.nome}>{c.nome.split('[')[1]?.replace(/\]$/, '')}</option>)}
                  </select>
                  {tabela && colunas.length === 0 && <small style={{ color: '#c4362b', fontWeight: 700 }}>Esta tabela não tem coluna do tipo data. Escolha a tabela de calendário.</small>}
                  {!tabela && <small>Só aparecem colunas do tipo data.</small>}
                </label>
                <label className="ng-campo"><span>Formato</span>
                  <select value={formato} onChange={e => setFormato(e.target.value)}>
                    <option value="numero">Número</option><option value="percentual">Percentual (0,87 = 87%)</option><option value="moeda">Moeda (R$)</option>
                  </select>
                </label>
                <label className="ng-campo"><span>Quando sobe</span>
                  <select value={maiorMelhor ? 'bom' : 'ruim'} onChange={e => setMaiorMelhor(e.target.value === 'bom')}>
                    <option value="bom">É bom (verde)</option><option value="ruim">É ruim (vermelho), como ruptura</option>
                  </select>
                </label>
              </div>
              {teste && (typeof teste === 'string'
                ? <p className="ng-frase" style={{ color: '#c4362b', marginTop: 12 }}>{teste}</p>
                : <PreviaIndicador i={teste} />)}
              <div className="ng-rodape-cartao">
                <button type="button" className="ng-btn" disabled={!pronto || ocupado} onClick={testar}>{ocupado && !teste ? 'Consultando…' : 'Testar'}</button>
                <button type="button" className="ng-btn prim" disabled={!pronto || !mudou || ocupado} onClick={() => salvar()}>Salvar indicador</button>
                {dash.kpiMeasure && <button type="button" className="ng-btn perigo" disabled={ocupado} onClick={() => salvar(true)}>Tirar indicador</button>}
              </div>
            </>
            )}
    </section>
  )
}

function PreviaIndicador({ i }: { i: Indicador }) {
  const v = variacao(i)
  const pontos = pontosDaLinha(i.serie, 220, 44)
  return (
    <div className="ng-frase" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <span>
        <b style={{ fontSize: 22, color: '#1d2230' }}>{formatar(i.valor, i.formato)}</b>{' '}
        {v && <span style={{ fontWeight: 700, color: v.bom == null ? '#5b6273' : v.bom ? '#2f8a46' : '#c4362b' }}>{v.texto}</span>}
        <small style={{ display: 'block' }}>Sem recorte (visão de administrador) · últimos 7 dias</small>
      </span>
      {pontos
        ? <svg width={220} height={44} viewBox="0 0 220 44" role="img" aria-label="12 semanas"><polyline points={pontos} fill="none" stroke="var(--np-primaria, #e21ae7)" strokeWidth="2" strokeLinejoin="round" /></svg>
        : <small>A medida veio vazia nas 12 semanas: confira a coluna de data.</small>}
    </div>
  )
}
